import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import AirOutlinedIcon from "@mui/icons-material/AirOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import DataGridTable from "../Components/DataGridTable";
import Alert from "../Components/Alert";
import endpoints from "../api/endpoints";
import "./Weather.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatNumber = (value, digits = 1) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : "-";
};

const formatStatus = (value) => {
  const label = String(value || "").trim();
  return label ? label.replace(/_/g, " ") : "-";
};

const formatDustFlag = (value) => {
  if (value === null || value === undefined) return "Unknown";
  return value ? "Yes" : "No";
};

const normalizeWeatherRow = (row) => ({
  ...row,
  fetchedAt: row.fetchedAt ? new Date(row.fetchedAt) : null,
  observedAt: row.observedAt ? new Date(row.observedAt) : null,
  updatedAt: row.updatedAt ? new Date(row.updatedAt) : null,
});

const Weather = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: "updatedAt", sort: "desc" }]);
  const [alertState, setAlertState] = useState({
    open: false,
    type: "info",
    message: "",
    position: "right",
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({ open: true, type, message, position: "right" });
  }, []);

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        endpoints.data.weather({
          page,
          pageSize,
          filters: JSON.stringify(filterModel),
          sortModel: JSON.stringify(sortModel),
        })
      );

      const items = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(items.map(normalizeWeatherRow));
      setRowCount(Number(res?.data?.pagination?.total || 0));
    } catch (err) {
      showAlert("error", err?.response?.data?.message || err?.message || "Failed to load weather data.");
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [filterModel, page, pageSize, showAlert, sortModel]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  useEffect(() => {
    setPage(1);
  }, [filterModel, sortModel]);

  const latestUpdate = useMemo(() => {
    const timestamps = rows
      .map((row) => row.updatedAt || row.fetchedAt)
      .filter(Boolean)
      .map((date) => date.getTime())
      .filter(Number.isFinite);

    if (!timestamps.length) return "-";
    return formatDateTime(Math.max(...timestamps));
  }, [rows]);

  const dustyCount = useMemo(() => rows.filter((row) => row.hasDust).length, [rows]);
  const failedCount = useMemo(() => rows.filter((row) => row.dataStatus === "failed").length, [rows]);
  const highestAqi = useMemo(() => {
    const values = rows.map((row) => Number(row.usAqi)).filter(Number.isFinite);
    return values.length ? Math.max(...values) : null;
  }, [rows]);

  const columns = useMemo(() => [
    {
      field: "emirateName",
      headerName: "Emirate",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "provider",
      headerName: "Provider",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dataStatus",
      headerName: "Data",
      minWidth: 120,
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatStatus(params?.value ?? params),
    },
    {
      field: "weatherStatus",
      headerName: "Weather",
      minWidth: 140,
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatStatus(params?.value ?? params),
    },
    {
      field: "dustStatus",
      headerName: "Dust",
      minWidth: 130,
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatStatus(params?.value ?? params),
    },
    {
      field: "airQualityStatus",
      headerName: "Air Quality",
      minWidth: 150,
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatStatus(params?.value ?? params),
    },
    {
      field: "hasDust",
      headerName: "Has Dust",
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatDustFlag(params?.value ?? params),
    },
    {
      field: "temperatureC",
      headerName: "Temp C",
      type: "number",
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatNumber(params?.value ?? params),
    },
    {
      field: "humidityPercent",
      headerName: "Humidity %",
      type: "number",
      minWidth: 125,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatNumber(params?.value ?? params, 0),
    },
    {
      field: "pm10Ugm3",
      headerName: "PM10",
      type: "number",
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatNumber(params?.value ?? params),
    },
    {
      field: "pm25Ugm3",
      headerName: "PM2.5",
      type: "number",
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatNumber(params?.value ?? params),
    },
    {
      field: "usAqi",
      headerName: "US AQI",
      type: "number",
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatNumber(params?.value ?? params, 0),
    },
    {
      field: "observedAt",
      headerName: "Observed",
      type: "dateTime",
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatDateTime(params?.value ?? params),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      type: "dateTime",
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatDateTime(params?.value ?? params),
    },
  ], []);

  return (
    <div className="weather-page">
      <Alert
        open={alertState.open}
        close={() => setAlertState((prev) => ({ ...prev, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className="weather-header">
        <div>
          <h1>Weather</h1>
          <p>Live UAE weather and dust status from the weather cron records.</p>
        </div>
        <button type="button" className="devices-primary-btn" onClick={fetchWeather} disabled={loading}>
          Refresh
        </button>
      </div>

      <div className="weather-summary-grid">
        <div className="weather-summary-item">
          <WbSunnyOutlinedIcon />
          <div>
            <span>Visible records</span>
            <strong>{rows.length}</strong>
          </div>
        </div>
        <div className="weather-summary-item">
          <AirOutlinedIcon />
          <div>
            <span>Dust / failed</span>
            <strong>{dustyCount} / {failedCount}</strong>
          </div>
        </div>
        <div className="weather-summary-item">
          <WaterDropOutlinedIcon />
          <div>
            <span>Highest AQI</span>
            <strong>{highestAqi === null ? "-" : formatNumber(highestAqi, 0)}</strong>
          </div>
        </div>
        <div className="weather-summary-item">
          <UpdateOutlinedIcon />
          <div>
            <span>Latest update</span>
            <strong>{latestUpdate}</strong>
          </div>
        </div>
      </div>

      <div className="weather-grid-shell">
        <DataGridTable
          rows={rows}
          columns={columns}
          idcolumn="id"
          pagingoptions={[10, 20, 50, 100]}
          sortField="updatedAt"
          sortType="desc"
          showToolbar
          tableKey="weather"
          loading={loading}
          pagination={{
            serverSide: true,
            page,
            pageSize,
            count: rowCount,
            sortModel,
            onSortModelChange: (nextSortModel) => {
              setSortModel(nextSortModel?.length ? nextSortModel : [{ field: "updatedAt", sort: "desc" }]);
            },
            onFilterModelChange: (nextFilterModel) => {
              setFilterModel(nextFilterModel || { items: [] });
            },
            onPageChange: setPage,
            onPageSizeChange: (nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            },
          }}
        />
      </div>
    </div>
  );
};

export default Weather;
